import { createSignal, onMount, onCleanup, Accessor } from 'solid-js';
import { isServer } from 'solid-js/web';

type Refs = {
    firstTabbableElement: Accessor<HTMLElement | undefined>;
    lastTabbableElement: Accessor<HTMLElement | undefined>;
    lastChildOfLastElement?: Accessor<HTMLElement | undefined>;
};

type TabHandlerProps = { 
    refs: Refs;
    lastElementIsAMenuAndIsOpen?: Accessor<boolean>;
    children: any
};

export const TabHandler = (props: TabHandlerProps) => {
    if (isServer) {
        return <>{props.children}</>;
    }

    const [ tabbedCount, setTabbedCount ] = createSignal(0);
    const [ triggerElement, setTriggerElement ] = createSignal<HTMLElement | undefined>(undefined);

    const {
        firstTabbableElement,
        lastTabbableElement,
        lastChildOfLastElement
    } = props.refs;

    onMount(() => {
        document && document.addEventListener('keydown', keyDownHandler);

        // This was the focused element when the component was mounted, e.g. the button that opened the modal
        setTriggerElement(document.activeElement as HTMLElement);
    });

    onCleanup(() => {
        document && document.removeEventListener('keydown', keyDownHandler);

        // Re-focus the element that was focused when the component was mounted, so that we keep the place on the page
        if (triggerElement()) {
            triggerElement()!.tabIndex = -1;
            triggerElement()!.focus();
            triggerElement()!.tabIndex = 0;
        }
    });

    const keyDownHandler = (event: KeyboardEvent) => {
        if (event.key !== 'Tab') return;

        tabHandler(event);
    };

    /**
     * Ensure tab navigation stays within the menu context
     */
    const tabHandler = (event: KeyboardEvent) => {        
        if (tabbedCount() === 0) {
            handleInitialTabbedEntry(event);
            setTabbedCount(1);
        }
    
        if (event.target === firstTabbableElement()) {
            handleFirstGroupNavigationOut(event);
        }
    
        const isTargetLastElement = event.target === lastTabbableElement();
        const isTargetLastChildOfLastElement = event.target === (lastChildOfLastElement && lastChildOfLastElement());
        const isLastElementMenuOpen = props.lastElementIsAMenuAndIsOpen && props.lastElementIsAMenuAndIsOpen();
    
        const isLastElementNavigationOut = 
            (isTargetLastElement && !isLastElementMenuOpen) ||
            (isTargetLastChildOfLastElement && isLastElementMenuOpen);

        if (isLastElementNavigationOut) {
            handleLastGroupNavigationOut(event);
        }
    };

    const handleInitialTabbedEntry = (event: KeyboardEvent) => {        
        event.preventDefault();
    
        if (!firstTabbableElement()) return;
    
        if (firstTabbableElement()) {
            firstTabbableElement()!.tabIndex = -1;
            firstTabbableElement()!.focus();
            firstTabbableElement()!.tabIndex = 0;
        }
    };
    
    /**
     * Handle moving focus from first tabbable element to the last tabbable element.
     */
    const handleFirstGroupNavigationOut = (event: KeyboardEvent) => {
        if (!event.shiftKey) return;
    
        const elementToFocus = props.lastElementIsAMenuAndIsOpen && props.lastElementIsAMenuAndIsOpen() 
            ? lastChildOfLastElement
            : lastTabbableElement;
    
        if (!elementToFocus || !elementToFocus()) return;
    
        event.preventDefault();
    
        elementToFocus()!.tabIndex = -1;
        elementToFocus()!.focus();
        elementToFocus()!.tabIndex = 0;
    };
    
    /**
     * Handle moving focus from last tabbable element to the first tabbable element.
     */
    const handleLastGroupNavigationOut = (event: KeyboardEvent) => {
        if (event.shiftKey) return;
    
        event.preventDefault();
    
        if (firstTabbableElement()) {
            firstTabbableElement()!.tabIndex = -1;
            firstTabbableElement()!.focus();
            firstTabbableElement()!.tabIndex = 0;
        }
    };

    return (
        <>
            {props.children}
        </>
    );
};
